import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  Calendar, Mic, RefreshCw, Bell, FileText, Layers, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const AppointmentSchedulingPage = () => {
  const benefits = [
    {
      icon: Calendar,
      title: '24/7 scheduling coverage across voice and chat',
      description:
        'Customers can book, reschedule, or cancel at any hour via voice or chat - no hold times, no business-hours restrictions, no agent availability required.',
    },
    {
      icon: CheckCircle2,
      title: 'Real-time availability checks with instant confirmation',
      description:
        'The AI queries live calendar and resource availability before offering slots - ensuring no double-booking and sending immediate confirmation once an appointment is set.',
    },
    {
      icon: Bell,
      title: 'Automated reminders reducing no-shows',
      description:
        'Configurable reminder sequences via SMS, email, and chat reduce no-show rates - sent at the intervals you define and updated automatically when appointments change.',
    },
  ];

  const capabilities = [
    'Integrate scheduling/calendar tool + CRM + notifications (SMS/email)',
    'Define rules: lead time, cancellations, reschedule windows, resource constraints',
    'Configure flows: schedule, reschedule, cancel, reminder, and follow-up',
    'Support voice + chat omnichannel booking',
    '24/7 scheduling coverage with real-time availability checks',
  ];

  const features = [
    {
      icon: Calendar,
      title: 'Real-Time Calendar Integration',
      desc: 'Connect to your scheduling system or calendar platform and query live availability before offering any slot - preventing conflicts and double-booking.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: Mic,
      title: 'Voice & Chat Booking',
      desc: 'Handle appointment requests via phone, web chat, and messaging channels - with a consistent booking experience regardless of how the customer reaches out.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: RefreshCw,
      title: 'Reschedule & Cancel Flows',
      desc: 'Let customers reschedule or cancel appointments within configured policy windows - automatically releasing the slot and offering alternatives when available.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: Bell,
      title: 'SMS / Email Reminders',
      desc: 'Send configurable reminder sequences at defined intervals before the appointment - via SMS, email, or chat - with automatic updates when appointments change.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: FileText,
      title: 'CRM Case Logging',
      desc: 'Every booking, change, and cancellation is logged as a CRM case or activity - maintaining a complete appointment history for each customer.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: Layers,
      title: 'Multi-Resource Scheduling',
      desc: 'Manage availability across multiple locations, technicians, vehicles, or service types - matching the right resource to the customer request automatically.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Customer requests appointment action',
      desc: 'The customer contacts via voice or chat to book, reschedule, or cancel a service appointment, delivery, consultation, repair, or callback.',
    },
    {
      step: '02',
      title: 'AI verifies identity as required',
      desc: 'For existing customers, the agent verifies identity per configured policy before accessing their appointment history or making changes to confirmed bookings.',
    },
    {
      step: '03',
      title: 'AI checks availability and books or reschedules',
      desc: 'The agent queries live calendar availability, applies resource and lead-time constraints, and offers available slots - confirming the selected appointment immediately.',
    },
    {
      step: '04',
      title: 'Sends confirmation and reminders',
      desc: 'An immediate confirmation is sent via the customer\'s preferred channel. The reminder sequence is scheduled automatically based on configured intervals.',
    },
    {
      step: '05',
      title: 'Logs update in CRM or case system',
      desc: 'The appointment booking, change, or cancellation is recorded in the CRM as a case activity - maintaining a complete history for the customer and service team.',
    },
  ];

  const stats = [
    { value: '3×', label: 'Faster response time' },
    { value: '~68%', label: 'Deflection for scheduling requests' },
    { value: '+22%', label: 'CSAT uplift' },
    { value: '+35%', label: 'Agent efficiency gain' },
  ];

  const faqs = [
    {
      q: 'Can it manage multiple locations and resources?',
      a: 'Yes. The scheduling engine supports multiple locations, resource types, and service categories. Rules define which resources are available at each location, their availability windows, and any customer-resource matching constraints - all enforced automatically.',
    },
    {
      q: 'Can it send reminders automatically?',
      a: 'Yes. Reminder sequences are fully configurable - you define how many reminders to send, at what intervals (e.g., 48 hours before, 24 hours before, 2 hours before), and via which channels (SMS, email, or chat). Reminders are automatically updated if the appointment is rescheduled.',
    },
    {
      q: 'What if the customer wants a specific agent or technician?',
      a: 'If a customer requests a specific resource, the agent checks that resource\'s availability and books the preferred slot if available. If not, it offers the next available slot for that resource or suggests an alternative - based on configured preference-handling rules.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseAppointmentScheduling" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Appointment Scheduling","item":"https://digian.ai/use-cases/appointment-scheduling"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">Customer Care / Appointment Scheduling &amp; Reminders</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              AI Scheduling Agent - Book, Reschedule, Remind (Voice + Chat)
            </h1>
            <p className="text-lg text-slate-600">
              Automate appointment booking and changes for services, deliveries, consultations, repairs, and callbacks - plus automated reminders and confirmations across voice and chat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['24/7 booking coverage', 'Real-time availability', 'Automated reminders'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Appointment Scheduling Automation</p>
                <p className="text-sm text-slate-600">Book, reschedule, remind - omnichannel, 24/7</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <Calendar className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why teams automate appointment scheduling</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate appointment management</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How AI appointment scheduling works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">Book, reschedule, remind - without an agent</h2>
            <p className="text-xl text-indigo-100">
              24/7 scheduling across voice and chat. Real-time availability. Automated reminders that reduce no-shows.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">Omnichannel · Real-time availability · Automated reminders</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AppointmentSchedulingPage;
